import ConfirmModal from "components/ConfirmModal";
import {MultiCombobox} from "components/Form/Form/Control";
import React, {useState} from "react";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import IconName from "types/IconName";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import type {Offer} from "types/Offer";
import type {OfferDistributionData} from "types/OfferDistribution";

// Types ----

type DistributeOfferProps = {
  offer: Offer | null
  show: boolean
  onCancel?: () => void
  onDistribute?: (offer: Offer, segmentIds: number[], userIds: number[]) => void
  onDistributeFail?: (offer: Offer, segmentIds: number[], userIds: number[], error: unknown) => void
}

// Helpers ----

const segmentOptions = (searchTerm: string | null) => ngage.audienceSegments
  .filtered({page: 1, perPage: 100, search: searchTerm})
  .then(page => page?.data || [])
  .then(segments => segments.map(segment => ({
    id: segment.id,
    name: segment.name,
    description: `~ ${segment.audience_size} people`,
    icon: IconName.USER_GROUP
  })))

const userOptions = (searchTerm: string | null) => ngage.users
  .filtered({page: 1, perPage: 100, search: searchTerm})
  .then(page => page?.data || [])
  .then(users => users.map(user => ({
    id: user.id,
    name: user.name,
    description: user.role_names.join(', '),
    icon: IconName.USER
  })))

// Component ----

function DistributeOffer({offer, show, onCancel, onDistribute, onDistributeFail}: DistributeOfferProps) {
  const {notify} = useNotifications();

  const [segmentSearchTerm, setSegmentSearchTerm] = useState<string | null>(null);
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);

  const [userSearchTerm, setUserSearchTerm] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const canDistribute = !!offer && (!!selectedSegmentIds.length || !!selectedUserIds.length);

  const {data: segments} = useQuery(
    ['segments', 'operator', segmentSearchTerm],
    () => segmentOptions(segmentSearchTerm),
    {keepPreviousData: true}
  );

  const {data: users} = useQuery(
    ['users', 'operator', userSearchTerm],
    () => userOptions(userSearchTerm),
    {keepPreviousData: true}
  );

  const {mutateAsync: distributeOffer} = useMutation(
    (data: OfferDistributionData) => ngage.offers.distribute(Number(offer?.id), data)
  )

  function handleCancel() {
    setSelectedSegmentIds([]);
    setSelectedUserIds([]);

    if (onCancel) onCancel();
  }

  async function handleDistributeOffer() {
    if (!canDistribute) return;

    try {
      await distributeOffer({segment_ids: selectedSegmentIds, user_ids: selectedUserIds});
      await invalidate('offers');

      notify("success", "Offer Distributed", `${offer.name} has been distributed to the selected recipients`)

      if (onDistribute) onDistribute(offer, selectedSegmentIds, selectedUserIds);

      setSelectedSegmentIds([]);
      setSelectedUserIds([]);

    } catch (error: any) {
      notify("error", "Offer Not Distributed", `${offer.name} could not be distributed to the selected recipients`)

      if (onDistributeFail) onDistributeFail(offer, selectedSegmentIds, selectedUserIds, error);
    }
  }

  return (
    <ConfirmModal
      show={show}
      title="Distribute Offer"
      body={`Select which people will receive a voucher for ${offer ? offer.name : 'The selected offer'}`}
      approveLabel="Distribute"
      onApprove={handleDistributeOffer}
      onCancel={handleCancel}
    >
      <div className="w-full mt-2">
        <MultiCombobox
          label={"Segments"}
          options={segments || []}
          selected={segments?.filter(segment => selectedSegmentIds.includes(segment.id))}
          onSearch={searchTerm => setSegmentSearchTerm(searchTerm ?? null)}
          onChange={segments => setSelectedSegmentIds(segments.map(segment => Number(segment.id)))}
        />
      </div>

      <div className="w-full mt-4">
        <MultiCombobox
          label={"Users"}
          options={users || []}
          selected={users?.filter(user => selectedUserIds.includes(user.id))}
          onSearch={searchTerm => setUserSearchTerm(searchTerm ?? null)}
          onChange={users => setSelectedUserIds(users.map(user => Number(user.id)))}
        />
      </div>
    </ConfirmModal>
  );
}

export default DistributeOffer;